import _ from "lodash";
import axios, { AxiosError, type AxiosRequestConfig, type Method } from "axios";
import { IApp } from "@/types";
import { toast, type ToastType } from "vue3-toastify";
import { type ValidationError } from "yup";
import moment from "moment";
import GoogleSearchConsoleSource from "@/sources/GoogleSearchConsole";
import BirdEyeSource from "@/sources/BirdEye";
import GoogleAnalyticsSource from "@/sources/GoogleAnalytics";
import remarkHtml from "remark-html";
import remarkParse from "remark-parse";
import { remark } from "remark";
import TurndownService from "turndown";

const turndownService = new TurndownService();

const axiosInstance = axios.create();

axiosInstance.interceptors.request.use((config) => {
  if (!config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${getBearerToken()}`;
  }
  return config;
});

export const apiURL = (url: string) => {
  const baseUrl = import.meta.env.VITE_API_URL ?? import.meta.env.VITE_LOCAL_API_URL ?? "";
  return `${baseUrl}/${_.trimStart(url, "/")}`;
};

export const authEndpoint = (path: string) => {
  const env = import.meta.env.VITE_ENV ?? "dev";
  const baseUrl = import.meta.env.VITE_AUTH_API_URL ?? `https://api.auth.${env}.10thor.ai`;
  return `${baseUrl}/${_.trimStart(path, "/")}`;
};

export const localApiURL = (url: string) => {
  return `${import.meta.env.VITE_LOCAL_API_URL}/${_.trimStart(url, "/")}`;
};

interface MAxiosRequestConfig extends AxiosRequestConfig {
  method: Method;
}

const POLL_INTERVAL_MS = 4000;
const MAX_POLL_ATTEMPTS = 45; // ~3 minutes

/** Polls the endpoint when backend returns status: "pending" (cache generation in progress) */
export const sendRequestWithAnalyzeAllPolling = async <T>(
  config: MAxiosRequestConfig
): Promise<IApp.IApiResponse<T>> => {
  for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
    const result = await sendRequest<T & { status?: string }>(config);
    if (!result.status) return result as IApp.IApiResponse<T>;
    const body = result.data as { status?: string; data?: unknown };
    if (body?.status !== "pending") return result as IApp.IApiResponse<T>;
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  return {
    status: false,
    message: "Request timed out. Cache generation is taking longer than expected.",
    title: "Timeout"
  } as IApp.IApiResponse<T>;
};

export const sendRequest = async <T>(
  config: MAxiosRequestConfig
): Promise<IApp.IApiResponse<T>> => {
  try {
    const response = await axiosInstance.request(config);
    return {
      status: true,
      data: response.data as T,
      headers: response.headers
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      return {
        status: false,
        title: error.response?.data.error,
        message: error.response?.data.message ?? "Something went wrong",
        type: error.response?.data.title
      };
    } else if (error instanceof Error) {
      return {
        status: false,
        title: "Unknown Error",
        message: error.message ?? "Something went wrong",
        type: Error.name
      };
    } else {
      return {
        title: "Unknown Error",
        status: false,
        message: "Something went wrong",
        type: "UnknownError"
      };
    }
  }
};

export const getBearerToken = () => {
  return localStorage.getItem("token");
};

export const setBearerToken = (token: string) => {
  localStorage.setItem("token", token);
};

export const clearBearerToken = () => {
  localStorage.removeItem("token");
};

export const formatNumber = (num: number, fractionDigits: number = 2): string => {
  return new Intl.NumberFormat("en-GB", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: 0
  }).format(num);
};

export const formatSecond = (sec: number): { value: string; unit: "min" } => {
  const minutes = Math.floor(sec / 60);
  const seconds = Math.floor(sec % 60);
  return {
    value: `${_.padStart(minutes.toString(), 2, "0")}:${_.padStart(seconds.toString(), 2, "0")}`,
    unit: "min"
  };
};

export const getAvatarName = (name: string): string => {
  const nameParts = name?.trim()?.split(" ");

  if (!nameParts) {
    return "";
  }

  if (nameParts.length === 1) {
    return nameParts[0].slice(0, 2).toUpperCase();
  } else {
    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  }
};

export const getFeedNameFromFeedType = (feed: IApp.IFeedTypes) => {
  switch (feed) {
    case IApp.IFeedTypes.GoogleSearchConsole:
      return "Google Search Console";
    case IApp.IFeedTypes.GoogleAnalytics:
      return "Google Analytics";
    case IApp.IFeedTypes.BirdEye:
      return "Bird Eye";
    default:
      return feed;
  }
};

export const makeAlert = (message: string, type: ToastType = "info") => {
  toast(message, {
    autoClose: 3000,
    hideProgressBar: true,
    type: type
  });
};

export const getAppEnv = () => {
  switch (import.meta.env.VITE_ENV) {
    case IApp.IEnvironments.Prod:
      return IApp.IEnvironments.Prod;
    case IApp.IEnvironments.Staging:
      return IApp.IEnvironments.Staging;
    case IApp.IEnvironments.Dev:
      return IApp.IEnvironments.Dev;
    case IApp.IEnvironments.Local:
      return IApp.IEnvironments.Local;
    default:
      throw new Error("Unknown environment");
  }
};

export const validate = <D, S extends { validateSync: Function }>(
  schema: S,
  data: D
): { status: true; data: D } | { status: false; message: string } => {
  try {
    return {
      status: true,
      data: schema.validateSync(data, { abortEarly: false })
    };
  } catch (error) {
    return {
      status: false,
      message: _.head((error as ValidationError).errors)?.toString() ?? "Validation failed"
    };
  }
};

export function isValid(
  account: IApp.IGoogleSearchConsoleAccount | IApp.IGoogleAnalyticsAccount | IApp.IBirdEyeAccount
): boolean {
  switch (account.feed) {
    case IApp.IFeedTypes.GoogleSearchConsole:
      return "siteUrls" in account.data && _.size(account.data.siteUrls) > 0;
    case IApp.IFeedTypes.BirdEye:
      return "businessId" in account.data && !!account.data.businessId && !!account.data.apiKey;
    case IApp.IFeedTypes.GoogleAnalytics:
      return "propertyId" in account.data && !!account.data.propertyId && !!account.data.accountId;
    default:
      return false;
  }
}

export const isCustomerValid = (customer: IApp.ICustomer) => {
  if (customer.deletedAt) {
    return false;
  }
  if (getAppEnv() !== IApp.IEnvironments.Local && _.endsWith(customer.name, "( Test )")) {
    return false;
  }
  return true;
};

export const customerHasAnyDataSource = (customer: IApp.ICustomer): boolean => {
  return (customer.accounts?.length ?? 0) > 0;
};

export function getPastNMonths(n: number, date: Date) {
  const pstTime = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    dateStyle: "full",
    timeStyle: "long"
  }).format(date);
  const res: string[] = [];
  for (let i = 0; i < n; i++) {
    res.push(
      moment(pstTime, "dddd, MMMM D, YYYY [at] h:mm:ss A z")
        .subtract(i + 1, "month")
        .format("YYYY-MM")
    );
  }

  return res.reverse();
}

export function* iterateMonths(start: string, end: string) {
  if (start < end) {
    const current = moment(start, "YYYY-MM");
    while (current.isSameOrBefore(end)) {
      yield current.format("YYYY-MM");
      current.add(1, "month");
    }
  } else {
    const current = moment(start, "YYYY-MM");
    while (current.isSameOrAfter(end)) {
      yield current.format("YYYY-MM");
      current.subtract(1, "month");
    }
  }
}

export function qs(
  params: Record<string, string | number | boolean | null | Array<string | number | boolean | null>>
) {
  const s = (key: string, value: string | number | boolean | null) => {
    return `${encodeURIComponent(key)}=${value === null ? "" : encodeURIComponent(value)}`;
  };
  return Object.keys(params)
    .map((key) => {
      if (Array.isArray(params[key])) {
        return params[key].map((v) => s(key, v)).join("&");
      } else {
        return s(key, params[key]);
      }
    })
    .join("&");
}
export function isSynced(
  account: IApp.IGoogleSearchConsoleAccount | IApp.IGoogleAnalyticsAccount | IApp.IBirdEyeAccount
) {
  switch (account.feed) {
    case IApp.IFeedTypes.GoogleSearchConsole: {
      const _account = new GoogleSearchConsoleSource();
      _account.syncs = account.syncs;
      return _account.isSynced();
    }
    case IApp.IFeedTypes.BirdEye: {
      const _account = new BirdEyeSource();
      _account.syncs = account.syncs;
      return _account.isSynced();
    }
    case IApp.IFeedTypes.GoogleAnalytics: {
      const _account = new GoogleAnalyticsSource();
      _account.syncs = account.syncs;
      return _account.isSynced();
    }
    default:
      return false;
  }
}

export function getDataTypeLabel(dataType: IApp.DataType) {
  switch (dataType) {
    case IApp.DataType.Default:
      return "Default";
    case IApp.DataType.BirdEyeReviews:
      return "Reviews";
    case IApp.DataType.BirdEyeListings:
      return "Listings";
    case IApp.DataType.GoogleAnalyticsTotal:
      return "Total";
    case IApp.DataType.GoogleAnalyticsPageViews:
      return "Page Views";
    case IApp.DataType.GoogleAnalyticsSessionCount:
      return "Session Count";
    case IApp.DataType.GoogleAnalyticsCityActiveUsers:
      return "City Active Users";
    case IApp.DataType.GoogleAnalyticsEventCount:
      return "Event Count";
    case IApp.DataType.GoogleAnalyticsBrowserDeviceEventActiveUsers:
      return "Browser Device Event Active Users";
    case IApp.DataType.GoogleAnalyticsDeviceActiveUsers:
      return "Device Active Users";
    case IApp.DataType.GoogleAnalyticsOrganicTotal:
      return "Organic Total";
    case IApp.DataType.GoogleAnalyticsOrganicCityActiveUsers:
      return "Organic City Active Users";
    case IApp.DataType.GoogleAnalyticsOrganicDeviceActiveUsers:
      return "Organic Device Active Users";
    default:
      return "";
  }
}

export function getRandomInteger(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function flatten(obj: any, parentKey = ""): any {
  return Object.keys(obj).reduce((acc, key) => {
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    if (typeof obj[key] === "object" && obj[key] !== null) {
      return { ...acc, ...flatten(obj[key], newKey) };
    }
    return { ...acc, [newKey]: obj[key] };
  }, {});
}

export function formatToShortMonth(date: string, onlyMonth: boolean = false): string {
  if (onlyMonth) {
    return moment(date, "YYYY-MM").format("MMM");
  }
  return moment(date, "YYYY-MM").format("MMM YYYY");
}

export function deFlatten(item: any) {
  const nested: any = {};
  Object.entries(item).forEach(([key, value]) => {
    _.set(nested, key, value);
  });
  return nested;
}

// @ts-ignore
export const convertMarkdownToHTML = marked;

export const isLoading = (status: IApp.ILoadingStatus) => {
  return status === IApp.ILoadingStatus.Loading;
};

export const copyTextToClipboard = async (text: string) => {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
  } else {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
  makeAlert("Copied to clipboard", "success");
};

export const withQueryString = (path: string, params: Record<string, any>) => {
  const url = new URL(path, window.location.origin);
  _.forEach(flatten(params), (value, key) => {
    url.searchParams.set(key, value);
  });
  return url.toString();
};

export function store<T>(data: Array<{ key: string; value: any }>, key: string, value: T): T {
  const index = _.findIndex(data, { key });
  if (index !== -1) {
    data[index].value = value;
  } else {
    data.push({ key, value });
  }
  return value;
}

export function getRelativeURL(url: string): string {
  const urlObj = new URL(url);
  return urlObj.pathname + urlObj.search + urlObj.hash;
}

export async function markdownToHtml(markdown: string): Promise<string> {
  const file = await remark().use(remarkHtml).use(remarkParse).process(markdown);
  return file.toString();
}

export function htmlToMarkdown(html: string): string {
  return turndownService.turndown(html);
}
